import {
  DEFAULT_APPEARANCE,
  THEME_COLOURS,
  isAppearanceMode,
  type AppearanceMode,
  type ResolvedAppearance,
} from './appearance';

/**
 * The DOM side of appearance.
 *
 * IndexedDB is the system of record for settings, but it is asynchronous — and
 * an async read cannot happen before first paint. A dark-mode user would get a
 * white flash on every launch, on every screen, forever.
 *
 * So the chosen mode is *mirrored* into localStorage, which is synchronous, and
 * read by a tiny inline script in index.html before the first paint. This is a
 * cache, never the source of truth: IndexedDB still wins on load.
 */

export const APPEARANCE_STORAGE_KEY = 'liftx.appearance';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * The device's current preference. No longer used to *follow* the device
 * live (there is no more `system` mode to do that) — only to give a legacy
 * `system` preference, or a brand-new device, one sensible one-time starting
 * point before the user picks explicitly.
 */
export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

/** Read the mirrored mode. Returns the default when absent or corrupt —
 *  including a pre-upgrade `system` value, which is no longer a valid mode. */
export function readMirroredAppearance(): AppearanceMode {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    return isAppearanceMode(raw) ? raw : DEFAULT_APPEARANCE;
  } catch {
    // Safari throws on storage access in some privacy configurations.
    return DEFAULT_APPEARANCE;
  }
}

export function writeMirroredAppearance(mode: AppearanceMode): void {
  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, mode);
  } catch {
    // A failed mirror costs a flash of the wrong theme, never correctness.
  }
}

/**
 * Apply an appearance to the document.
 *
 * `data-theme` and `data-resolved` always carry the same value now that
 * every mode is already concrete — kept as two attributes (rather than
 * collapsing to one) only because `tokens.css` and a couple of tests already
 * key off `data-resolved` specifically, and duplicating a value costs
 * nothing.
 */
export function applyAppearance(mode: AppearanceMode): ResolvedAppearance {
  const root = document.documentElement;

  root.setAttribute('data-theme', mode);
  root.setAttribute('data-resolved', mode);

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOURS[mode]);

  return mode;
}
